/**
 * Checkout-optimization billing schemas, previously defined in
 * redo/model/src/checkouts/checkout-optimization-settings.ts. Moved here so
 * the rest of redo/billing/common/ can reference them without depending on
 * redo/model. The settings file re-exports for back-compat.
 */
import { CheckoutOptimizationUsagePricingMode } from "@redotech/congo-enums/checkout-optimization-usage-pricing-mode";
import { zUsdCents, zUsdDollars } from "@redotech/money/usd-amounts";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";
import { PaymentProviderType } from "./billing-engine-types";
import {
  OrderQualifierKind,
  PopulationQualifierKind,
} from "./checkout-optimization-qualifier";
import {
  BillingPeriod,
  CheckoutOptimizationBillingPeriod,
} from "./service-invoice";

/**
 * Per-qualifier USD dollar rates (all checkout-optimization billing is USD).
 * An order matching several is charged once, at the max.
 */
const usdRate = z.number().nonnegative().pipe(zUsdDollars);
export const qualifierPricingSchema = z.object({
  [PopulationQualifierKind.PROVEN_LIFT]: usdRate.optional(),
  [PopulationQualifierKind.CUSTOM_RATE_TABLE]: usdRate.optional(),
  [PopulationQualifierKind.REDO_ONLY_SEGMENTATION]: usdRate.optional(),
  [OrderQualifierKind.REDO_SHIPPING_METHODS]: usdRate.optional(),
  [OrderQualifierKind.WIDGETS]: usdRate.optional(),
  [OrderQualifierKind.POST_PURCHASE_UPSELL]: usdRate.optional(),
  [OrderQualifierKind.POST_PURCHASE_UPSELL_ACCEPTED]: usdRate.optional(),
  [PopulationQualifierKind.SHIPPING_AI]: usdRate.optional(),
  [OrderQualifierKind.INTENT]: usdRate.optional(),
});
export type QualifierPricing = z.infer<typeof qualifierPricingSchema>;

/** Window columns are flat per kind; the domain shape is keyed by kind. */
export interface QualifierPriceColumns {
  qualifierPriceProvenLiftAmount?: number | undefined;
  qualifierPriceCustomRateTableAmount?: number | undefined;
  qualifierPriceRedoOnlySegmentationAmount?: number | undefined;
  qualifierPriceRedoShippingMethodsAmount?: number | undefined;
  qualifierPriceWidgetsAmount?: number | undefined;
  qualifierPricePostPurchaseUpsellAmount?: number | undefined;
  qualifierPricePostPurchaseUpsellAcceptedAmount?: number | undefined;
  qualifierPriceShippingAiAmount?: number | undefined;
  qualifierPriceIntentAmount?: number | undefined;
}

export function qualifierPricingToColumns(
  pricing: QualifierPricing | undefined,
): QualifierPriceColumns {
  return {
    qualifierPriceProvenLiftAmount:
      pricing?.[PopulationQualifierKind.PROVEN_LIFT],
    qualifierPriceCustomRateTableAmount:
      pricing?.[PopulationQualifierKind.CUSTOM_RATE_TABLE],
    qualifierPriceRedoOnlySegmentationAmount:
      pricing?.[PopulationQualifierKind.REDO_ONLY_SEGMENTATION],
    qualifierPriceRedoShippingMethodsAmount:
      pricing?.[OrderQualifierKind.REDO_SHIPPING_METHODS],
    qualifierPriceWidgetsAmount: pricing?.[OrderQualifierKind.WIDGETS],
    qualifierPricePostPurchaseUpsellAmount:
      pricing?.[OrderQualifierKind.POST_PURCHASE_UPSELL],
    qualifierPricePostPurchaseUpsellAcceptedAmount:
      pricing?.[OrderQualifierKind.POST_PURCHASE_UPSELL_ACCEPTED],
    qualifierPriceShippingAiAmount:
      pricing?.[PopulationQualifierKind.SHIPPING_AI],
    qualifierPriceIntentAmount: pricing?.[OrderQualifierKind.INTENT],
  };
}

/** Reads may carry explicit nulls from the driver; writes never do. */
export type QualifierPriceColumnsRead = {
  [K in keyof QualifierPriceColumns]?: QualifierPriceColumns[K] | null;
};

export function qualifierPricingFromColumns(
  columns: QualifierPriceColumnsRead | undefined | null,
): QualifierPricing {
  const usd = (amount: number | null | undefined) =>
    amount == null ? undefined : zUsdDollars.parse(amount);
  return {
    [PopulationQualifierKind.PROVEN_LIFT]: usd(
      columns?.qualifierPriceProvenLiftAmount,
    ),
    [PopulationQualifierKind.CUSTOM_RATE_TABLE]: usd(
      columns?.qualifierPriceCustomRateTableAmount,
    ),
    [PopulationQualifierKind.REDO_ONLY_SEGMENTATION]: usd(
      columns?.qualifierPriceRedoOnlySegmentationAmount,
    ),
    [OrderQualifierKind.REDO_SHIPPING_METHODS]: usd(
      columns?.qualifierPriceRedoShippingMethodsAmount,
    ),
    [OrderQualifierKind.WIDGETS]: usd(columns?.qualifierPriceWidgetsAmount),
    [OrderQualifierKind.POST_PURCHASE_UPSELL]: usd(
      columns?.qualifierPricePostPurchaseUpsellAmount,
    ),
    [OrderQualifierKind.POST_PURCHASE_UPSELL_ACCEPTED]: usd(
      columns?.qualifierPricePostPurchaseUpsellAcceptedAmount,
    ),
    [PopulationQualifierKind.SHIPPING_AI]: usd(
      columns?.qualifierPriceShippingAiAmount,
    ),
    [OrderQualifierKind.INTENT]: usd(columns?.qualifierPriceIntentAmount),
  };
}

/** Whether any kind is priced — an all-empty object bills nothing. */
export function hasAnyQualifierPrice(
  pricing: QualifierPricing | null | undefined,
): boolean {
  return (
    !!pricing && Object.values(pricing).some((price) => price !== undefined)
  );
}

/**
 * Self-serve free-trial product path on chop billing. Extensible for future
 * trial variants. Absent / {@link ConversionBillingTrialType.NONE} means the
 * team is not on unpaid-trial enforcement.
 *
 * Trial end is still {@link baseConversionBillingSchema}'s `startDate`
 * (billing begins then); there is no separate trialEndsAt.
 */
export enum ConversionBillingTrialType {
  NONE = "none",
  INTENT = "intent",
  INTENT_PPU = "intent-ppu",
}

export function isSelfServeBillingTrial(
  trialType: ConversionBillingTrialType | null | undefined,
): boolean {
  return (
    trialType === ConversionBillingTrialType.INTENT ||
    trialType === ConversionBillingTrialType.INTENT_PPU
  );
}

/**
 * Accept both the engine-shared `BillingPeriod.BIMONTHLY` and the legacy
 * `CheckoutOptimizationBillingPeriod.BI_WEEKLY` literal during the cadence
 * alignment migration. Existing team docs store `"bi-weekly"`; new writes
 * use `"bimonthly"`. The transform normalizes both to BIMONTHLY at read
 * time so callers always see the engine-standard value.
 *
 * Once the population-wide migration to `"bimonthly"` is complete, this can
 * be tightened to `z.literal(BillingPeriod.BIMONTHLY)`.
 */
const usagePeriodSchema = z
  .union([
    z.literal(BillingPeriod.BIMONTHLY),
    z.literal(CheckoutOptimizationBillingPeriod.BI_WEEKLY),
  ])
  .transform((): BillingPeriod.BIMONTHLY => BillingPeriod.BIMONTHLY);

const baseConversionBillingSchema = z.object({
  enabled: z.boolean(),
  startDate: z.date().optional(),
  renewOnPeriodEnd: z.boolean(),
  paymentProvider: createEnum(PaymentProviderType, {
    enumName: "PaymentProviderType",
  }).optional(),
  /**
   * Target day-of-month [1, 28] this product should bill on. When set and
   * different from the current invoice end day, the engine generates a
   * prorated transition invoice on the next cycle to land on this day.
   */
  targetBillingDay: z.number().min(1).max(28).optional(),
  /**
   * If true, any order whose checkout ran the Redo intent/hazard system on
   * the storefront becomes billable — even if the shopper never saw the
   * rescue panel and never picked a Redo shipping line. Detected via the
   * `redo_intent_active` cart attribute (scrubbed from the order before the
   * merchant ever sees it).
   */
  billIntentActiveOrders: z.boolean().optional(),
  /**
   * If set, bill the configured percentage (0, 100] of orders attributed to
   * intent. Absence means the percentage-attributed billing mode is off;
   * presence implies it is on at the stored percent.
   */
  billIntentAttributedOrdersPercent: z.number().gt(0).lte(100).optional(),
  /**
   * Self-serve free-trial product path (e.g. activateIntentRescue → intent-ppu).
   * Used by unpaid-trial enforcement; not a presentation lock signal.
   */
  trialType: createEnum(ConversionBillingTrialType, {
    enumName: "ConversionBillingTrialType",
  }).optional(),
});

export const conversionUsageBillingSchema = baseConversionBillingSchema.extend({
  /**
   * For backwards compatibility, we allow the type to be undefined as
   * this field didn't exist in the past and all existing records have a type of "usage".
   */
  billingType: z.literal("usage"),
  period: usagePeriodSchema,
  pricePerOrderInCents: z.number(),
  /** Absent means Flat: the shape every pre-existing record is on. */
  usagePricingMode: createEnum(CheckoutOptimizationUsagePricingMode, {
    enumName: "CheckoutOptimizationUsagePricingMode",
  }).optional(),
  qualifierPricing: qualifierPricingSchema.optional(),
});
export type CheckoutOptimizationUsageBilling = z.infer<
  typeof conversionUsageBillingSchema
>;

export const conversionSubscriptionBillingSchema =
  baseConversionBillingSchema.extend({
    billingType: z.literal("subscription"),
    period: z.literal(BillingPeriod.MONTHLY),
    periodFeeInCents: zUsdCents,
    pricePerOrderInCents: zUsdCents.optional(),
  });
export type CheckoutOptimizationSubscriptionBilling = z.infer<
  typeof conversionSubscriptionBillingSchema
>;

export const checkoutOptimizationBillingSchema = z.union([
  conversionUsageBillingSchema,
  conversionSubscriptionBillingSchema,
]);
export type CheckoutOptimizationBilling = z.infer<
  typeof checkoutOptimizationBillingSchema
>;
